import "./style.scss";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { formatDatesChosen } from "components/Payment";
import StatusChip from "components/shared/StatusChip";
import StyledButton from "components/shared/StyledButton";
import { StyledDatePicker } from "components/shared/StyledDatePicker";
import { StyledMenuItem, StyledSelect } from "components/shared/StyledSelect";
import StyledTextField from "components/shared/StyledTextField/StyledTextField";
import { H3 } from "helpers/TypographyComponents";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { IDatesRange } from "types";

type PreferredDate = "payment" | "statement" | "custom";

type OnSubmitParams = {
    titleId?: string | number;
    startDate?: Date | string;
    endDate?: Date | string;
    preferredDate: PreferredDate;
    date?: Date;
    preferredExpiryDate: boolean | Date;
};

interface AccountingModalProps {
    origin: "parution" | "payment";
    datesRange?: IDatesRange | undefined;
    titleId?: string | number;
    onSubmit: (object: OnSubmitParams) => void;
}

const AccountingModal = ({ origin, titleId, datesRange, onSubmit }: AccountingModalProps) => {
    const [t] = useTranslation();

    /**
     * Let user select their preferred date to appear on accounting export
     * - `payment` stands for payment date
     * - `statement` stands for statement creation date
     * - `custom` lets user select a custom date on their own
     */
    const [preferredDate, setPreferredDate] = useState<PreferredDate>("payment");
    /**
     * The custom date if applicable (cf above)
     */
    const [date, setDate] = useState<Date>(new Date());
    /**
     * Let user select a different expiry date than the one above, if `Date` type then it's a custom one
     * else it's a boolean
     */
    const [preferredExpiryDate, setPreferredExpiryDate] = useState<Date | boolean>(false);

    const handleSubmit = () => {
        // Send UTC midnight of the local calendar day so the backend stores the
        // intended day regardless of the user's timezone.
        const normalizedExpiryDate =
            preferredExpiryDate !== false && preferredExpiryDate instanceof Date
                ? new Date(
                      Date.UTC(
                          preferredExpiryDate.getFullYear(),
                          preferredExpiryDate.getMonth(),
                          preferredExpiryDate.getDate(),
                      ),
                  )
                : preferredExpiryDate;
        switch (origin) {
            case "parution":
                onSubmit({
                    titleId,
                    preferredDate,
                    date,
                    preferredExpiryDate: normalizedExpiryDate,
                });
                break;
            case "payment":
                onSubmit({
                    startDate: datesRange?.startDate,
                    endDate: datesRange?.endDate,
                    preferredDate,
                    date,
                    preferredExpiryDate: normalizedExpiryDate,
                });
                break;
            default:
                throw new Error("Unhandled yet.");
        }
    };

    return (
        <div className="accounting-modal-wrapper">
            <div>
                <span className="accounting-modal-info">
                    {t("Payment.ExportAccountingText1")}
                    <StatusChip
                        status={0}
                        statusRules={{ 0: { color: "green", text: t("Filters.Paid") } }}
                        style={{ marginInline: "5px", height: "fit-content", display: "inline-block" }}
                    />
                    {origin === "payment" && (
                        <>
                            {t("Payment.ExportAccountingTextOr")}
                            <StatusChip
                                status={0}
                                statusRules={{ 0: { color: "orange", text: t("Filters.ForTransfer") } }}
                                style={{ marginInline: "5px", height: "fit-content", display: "inline-block" }}
                            />
                        </>
                    )}
                    {t("Payment.ExportAccountingText2", { dates: formatDatesChosen(datesRange, true) })}
                </span>
                <div>
                    <H3 color="grey">{t("Accounting.ExportModal.PreferredDate")}</H3>
                    <StyledSelect
                        fullWidth
                        name="exportType"
                        className="entry-select"
                        value={preferredDate}
                        IconComponent={ExpandMoreIcon}
                        onChange={(e) => setPreferredDate(e.target.value as PreferredDate)}
                    >
                        <StyledMenuItem
                            value="payment"
                            sx={{
                                fontFamily: "inherit",
                            }}
                        >
                            {t("Accounting.ExportModal.PaymentDate")}
                        </StyledMenuItem>
                        <StyledMenuItem
                            value="statement"
                            sx={{
                                fontFamily: "inherit",
                            }}
                        >
                            {t("Accounting.ExportModal.StatementDate")}
                        </StyledMenuItem>
                        <StyledMenuItem
                            value="custom"
                            sx={{
                                fontFamily: "inherit",
                            }}
                        >
                            {t("Accounting.ExportModal.CustomDate")}
                        </StyledMenuItem>
                    </StyledSelect>
                </div>

                {preferredDate === "custom" && (
                    <div>
                        <H3 color="grey">{t("Accounting.ExportModal.PieceDate")}</H3>
                        <StyledDatePicker
                            inputFormat="dd/MM/yyyy"
                            value={date}
                            minDate={
                                new Date("2022-01-01")
                            } /* Some clients we migrate have payments earlier than their creationDate on this new version */
                            onChange={(date) => setDate(date)}
                            renderInput={(params) => <StyledTextField fullWidth {...params} />}
                        />
                    </div>
                )}
            </div>

            <div>
                <div>
                    <H3 color="grey" style={{ paddingLeft: 8 }}>
                        {t("Accounting.ExportModal.CustomExpiryDate")}
                    </H3>
                    <StyledSelect
                        fullWidth
                        name="exportType"
                        className="entry-select"
                        value={!!preferredExpiryDate}
                        IconComponent={ExpandMoreIcon}
                        onChange={(e) => setPreferredExpiryDate(e.target.value ? new Date() : false)}
                    >
                        <StyledMenuItem value={false as any}>{t("Accounting.ExportModal.No")}</StyledMenuItem>
                        <StyledMenuItem value={true as any}>{t("Accounting.ExportModal.Yes")}</StyledMenuItem>
                    </StyledSelect>
                </div>
                {!!preferredExpiryDate && (
                    <div>
                        <H3 color="grey" style={{ paddingLeft: 8 }}>
                            {t("Accounting.ExportModal.ExpiryDate")}
                        </H3>
                        <StyledDatePicker
                            inputFormat="dd/MM/yyyy"
                            value={preferredExpiryDate}
                            minDate={
                                new Date("2022-01-01")
                            } /* Some clients we migrate have payments earlier than their creationDate on this new version */
                            onChange={(date) => setPreferredExpiryDate(date)}
                            renderInput={(params) => <StyledTextField fullWidth {...params} />}
                        />
                    </div>
                )}
            </div>

            <StyledButton buttonType="primary" fullWidth onClick={handleSubmit}>
                {t("Accounting.ExportModal.Download")}
            </StyledButton>
        </div>
    );
};

export default AccountingModal;
